import * as tg from "generic-type-guard";
import { call, put, SagaGenerator, select, takeMaybe } from "typed-redux-saga";
import { AnyAction } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { DocumentSpec, JsonRpc2Request } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormActivity, FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";

import { createCustomNotificationAction } from "../app/actions";
import { pollDocumentRetries, pollDocumentTimeoutMs, requestRetryAttempts, requestRetryDelayMs } from "../config";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../localization";
import { ANTRAG_DETAIL_ACTIVITY_ID, TASK_DETAIL_ACTIVITY_ID } from "../modules/processOverview/store/actions";
import {
	isDocumentWithProcessMetadata,
	ProcessMetadataContainingDocument
} from "../modules/workflows/data/processMetadata";

import { toDateToPrettyString } from "./dateTimeUtils";
import { getDocument, isJsonRpc2ResponseArray } from "./rpcHelper";
import {
	fetchFromServerConnector,
	fetchFromServerConnectorWithRetry,
	isGatewayTimeoutError,
	isGatewayTimeoutResponse,
	UnexpectedServerResponseError
} from "./restHelper";
import { isString } from "./typeGuardUtils";
import { PATH_TO_VALIDATION_TYP } from "./variables/documentModelPaths";

const isTaskWithId = new tg.IsInterface()
	.withProperties({
		ID: tg.isString
	})
	.get();

const isA12wfWithTask = new tg.IsInterface()
	.withProperties({
		Task: isTaskWithId
	})
	.get();

function waitForPollingInterval(): Promise<void> {
	return new Promise(resolve => setTimeout(resolve, pollDocumentTimeoutMs));
}

export const isDocumentContainingA12wf = new tg.IsInterface()
	.withProperties({
		A12WF: isA12wfWithTask
	})
	.get();

export const VALIDATION_ACTIONS = {
	NULL: "NULL", // There will be no validation
	// TODO Brauchen wir diesen State überhaupt noch? Wir können nur den aktuell Prozessschritt bearbeiten,
	//  daher sollte alles vorherige bereits valid sein.
	SAVE: "SAVE", // Only the steps in the validation marker array will be validated
	COMPLETE: "COMPLETE" // The current step and the steps in the validation marker array will be validated.
};

/**
 * Executes a saga generator function while the application is set busy.
 * @param func the function to be executed while the application is busy.
 */
export function* executeBusy<T>(func: () => SagaGenerator<T>): SagaGenerator<T> {
	yield* put(ApplicationActions.setBusy(true));
	try {
		return yield* func();
	} finally {
		yield* put(ApplicationActions.setBusy(false));
	}
}

const RETRY = Symbol("retry");

function* waitIfAttemptsRemain(count: number, maxAttempts: number): SagaGenerator<void> {
	if (count < maxAttempts) {
		yield* call(waitForPollingInterval);
	}
}

function* fetchDocumentOrRetryOnGatewayTimeout(
	docRef: string,
	count: number,
	maxAttempts: number
): SagaGenerator<Partial<DocumentWithPermission> | typeof RETRY> {
	try {
		return yield* call(getDocumentWithPermission, docRef, 1);
	} catch (error) {
		if (!isGatewayTimeoutError(error)) {
			throw error;
		}
		yield* waitIfAttemptsRemain(count, maxAttempts);
		return RETRY;
	}
}

/** True once the document reflects a newer task than `oldTaskId` (and, if given, matches the process's current task). */
function hasAdvancedToExpectedTask(document: unknown, oldTaskId: string | undefined): boolean {
	if (!isDocumentContainingA12wf(document) || document.A12WF.Task.ID === oldTaskId) {
		return false;
	}
	return (
		oldTaskId === undefined ||
		(isDocumentWithProcessMetadata(document) && document.root.processMetadata.task.id === document.A12WF.Task.ID)
	);
}

function isProcessFinished(document: unknown): boolean {
	return isDocumentWithProcessMetadata(document) && document.root.processMetadata.isFinished;
}

export function* pullWfDocumentWithMetadata(
	docRef: string,
	oldTaskId?: string,
	maxAttempts: number = pollDocumentRetries
): SagaGenerator<DocumentWithPermission | undefined> {
	let count = 0;

	while (count++ < maxAttempts) {
		const result = yield* fetchDocumentOrRetryOnGatewayTimeout(docRef, count, maxAttempts);
		if (result === RETRY) {
			continue;
		}

		const { docSpec, hasPermission } = result;
		if (!docSpec) {
			return undefined;
		}

		if (hasAdvancedToExpectedTask(docSpec.document, oldTaskId) || isProcessFinished(docSpec.document)) {
			return { docSpec, hasPermission };
		}

		yield* waitIfAttemptsRemain(count, maxAttempts);
	}

	return undefined;
}

export type DocumentWithPermission = { docSpec: DocumentSpec; hasPermission?: boolean };

const reconciledCompletionStates = new Map<string, DocumentWithPermission>();

export function storeReconciledCompletionState(
	activityId: string,
	docRef: string,
	state: DocumentWithPermission
): void {
	reconciledCompletionStates.set(`${activityId}:${docRef}`, state);
}

export function takeReconciledCompletionState(activityId: string, docRef: string): DocumentWithPermission | undefined {
	const key = `${activityId}:${docRef}`;
	const state = reconciledCompletionStates.get(key);
	reconciledCompletionStates.delete(key);
	return state;
}

const GET_DOCUMENT_REQUEST_ID = "GET_DOCUMENT";
const HAS_PERMISSION_REQUEST_ID = "HAS_PERMISSION";

export function* getDocumentWithPermission(
	docRef: string,
	attempts: number = requestRetryAttempts
): SagaGenerator<Partial<DocumentWithPermission>> {
	const requests = [
		{
			id: GET_DOCUMENT_REQUEST_ID,
			jsonrpc: "2.0" as const,
			method: "GET_DOCUMENT",
			params: { docRef }
		},
		{
			id: HAS_PERMISSION_REQUEST_ID,
			jsonrpc: "2.0" as const,
			method: "MODULF_TASK_HAS_PERMISSION",
			params: { docRef }
		}
	];

	const payload = JsonRpc2Request.build(requests);
	const payloadWithHeaders = {
		...payload,
		customHeaders: [...(payload.customHeaders ?? []), ["Accept-Language", "de"] as [string, string]]
	};

	const response = yield* call(() =>
		fetchFromServerConnectorWithRetry(payloadWithHeaders, {
			attempts,
			delayMs: requestRetryDelayMs,
			shouldRetryResponse: isGatewayTimeoutResponse,
			fetchRequest: fetchFromServerConnector
		})
	);

	if (!isJsonRpc2ResponseArray(response)) {
		throw new UnexpectedServerResponseError("The document response cannot be interpreted.");
	}

	const getDocumentResponse = response.find(r => r.id === GET_DOCUMENT_REQUEST_ID);
	const hasRoleResponse = response.find(r => r.id === HAS_PERMISSION_REQUEST_ID);

	let hasPermission: boolean | undefined;

	if (!getDocumentResponse || getDocumentResponse.error) {
		throw new UnexpectedServerResponseError("The document response is missing or contains an error.", {
			cause: getDocumentResponse?.error
		});
	}
	const docSpec = getDocumentResponse.result as DocumentSpec;

	if (hasRoleResponse && !hasRoleResponse.error && typeof hasRoleResponse.result === "boolean") {
		hasPermission = hasRoleResponse.result;
	}

	return { docSpec, hasPermission };
}

export function* updateWorkflowsActivityData(document: object) {
	const taskDetailActivity = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
	if (taskDetailActivity) {
		yield* put(ActivityActions.setData({ activityId: TASK_DETAIL_ACTIVITY_ID, data: { document } }));
		yield* put(ActivityActions.setDirty({ activityId: TASK_DETAIL_ACTIVITY_ID, dirty: false }));
	}

	const antragDetailActivity = yield* select(ActivitySelectors.activityById(ANTRAG_DETAIL_ACTIVITY_ID));
	if (antragDetailActivity) {
		yield* put(ActivityActions.setData({ activityId: ANTRAG_DETAIL_ACTIVITY_ID, data: { document } }));
		yield* put(ActivityActions.setDirty({ activityId: ANTRAG_DETAIL_ACTIVITY_ID, dirty: false }));
	}
}

export function createEnableValidationStateFormEngineAction(activityId: string, action: string): AnyAction {
	return FormEngineActions.event({
		activityId,
		engineEvent: createEnableValidationStateAction(action)
	});
}

export function createEnableValidationStateAction(action: string): AnyAction {
	return Events.valueChange({
		path: PATH_TO_VALIDATION_TYP.map(e => ({ ...e, index: 1 })),
		value: action
	});
}

export function* reloadTaskAndAntragActivity(): SagaGenerator<void> {
	const taskActivity = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
	if (!taskActivity) {
		// the task activity must always be available
		return;
	}

	yield* put(
		ActivityActions.loadData({ activityId: taskActivity.id, dataHolderDescriptors: [taskActivity.descriptor] })
	);
	yield* takeMaybe(
		(action: AnyAction) => ActivityActions.setData.match(action) && action.payload.activityId === taskActivity.id
	);

	yield* syncAntragsActivityData();
}

export function* syncAntragsActivityData() {
	const antragDetailActivity = yield* select(ActivitySelectors.activityById(ANTRAG_DETAIL_ACTIVITY_ID));
	if (!antragDetailActivity) {
		return;
	}
	const data = yield* select(ActivitySelectors.data(TASK_DETAIL_ACTIVITY_ID));
	if (!FormActivity.Data.SingleDocumentData.isInstance(data)) {
		return;
	}

	yield* put(ActivityActions.setData({ activityId: ANTRAG_DETAIL_ACTIVITY_ID, data }));
	yield* put(ActivityActions.setDirty({ activityId: ANTRAG_DETAIL_ACTIVITY_ID, dirty: false }));
}

export function* handleIfOutdated(): SagaGenerator<boolean> {
	const activity = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
	if (!activity?.descriptor.instance) {
		return false;
	}

	const databaseDocument = (yield* call(getDocument, activity.descriptor.instance))?.document;
	if (!isDocumentWithProcessMetadata(databaseDocument)) {
		return false;
	}

	const isOutdated = yield* call(isOutdatedWorkflowActivity, activity, databaseDocument);
	if (isOutdated) {
		yield* call(handleOutdatedWorkflowActivity, activity, databaseDocument);
	}

	return isOutdated;
}

function isOutdatedWorkflowActivity(activity: Activity, databaseDocument: ProcessMetadataContainingDocument): boolean {
	if (
		(activity.id !== TASK_DETAIL_ACTIVITY_ID && activity.id !== ANTRAG_DETAIL_ACTIVITY_ID) ||
		!activity.descriptor.instance
	) {
		return false;
	}

	const activityData = Activity.findDefaultDataHolder(activity)?.data;
	if (
		!FormActivity.Data.SingleDocumentData.isInstance(activityData) ||
		!isDocumentWithProcessMetadata(activityData.document)
	) {
		return false;
	}

	const activityLastChangeOn = getTime(activityData.document.root.processMetadata.lastChangedOn);
	const databaseLastChangeOn = getTime(databaseDocument.root.processMetadata.lastChangedOn);

	return activityLastChangeOn !== databaseLastChangeOn;
}

function getTime(date: string | Date): number {
	return isString(date) ? new Date(date).getTime() : date.getTime();
}

export function* handleOutdatedWorkflowActivity(
	activity: Activity,
	databaseDocument?: object | undefined
): SagaGenerator<void> {
	if (!activity?.descriptor.instance) {
		return;
	}

	databaseDocument = (yield* call(getDocument, activity.descriptor.instance))?.document;
	if (!isDocumentWithProcessMetadata(databaseDocument)) {
		return;
	}

	const processMetadata = databaseDocument.root.processMetadata;
	const lastChangeOn = isString(processMetadata.lastChangedOn)
		? new Date(processMetadata.lastChangedOn)
		: processMetadata.lastChangedOn;
	const dateTimeString = toDateToPrettyString(lastChangeOn);

	yield* put(
		createCustomNotificationAction({
			title: localizableFromLocaleResources(RESOURCE_KEYS.wf.notification.error.savingFailedTitle),
			message: localizableFromLocaleResources(RESOURCE_KEYS.wf.notification.error.conflictedSavingMessage, {
				user: { type: "plain", value: processMetadata.lastChangedByName },
				vorgangsnummer: { type: "plain", value: processMetadata.vorgangsnummer },
				dateTime: { type: "plain", value: dateTimeString }
			}),
			severity: "error",
			group: `outdatedWorkflow:${databaseDocument.id}`
		})
	);

	yield* put(ActivityActions.cancel({ activityId: activity.id }));
	yield* put(ApplicationActions.setBusy(false));
}
